"use client";

import { useState } from "react";

import { pushEvent } from "@/lib/analytics-events";

type Status = "idle" | "submitting" | "ok" | "error";

/**
 * Email signup. Posts to /api/newsletter when wired; until then, the
 * route returns a placeholder 200 and the user sees the success state
 * without any subscription persistence. The component is intentionally
 * cautious: optimistic UI is reserved for confirmed persistence.
 *
 * Defaults render the footer (on-ink) variant; `variant="light"` restyles
 * for pale editorial surfaces. `tag` segments the signup at the provider
 * (e.g. "Auckland" for the pop-up clinic list).
 */
export function NewsletterSignup({
  tag,
  variant = "ink",
  buttonLabel = "Subscribe",
  note = "Infrequent by design. Treatment notes, journal updates, clinic news. Unsubscribe anytime.",
}: {
  tag?: string;
  variant?: "ink" | "light";
  buttonLabel?: string;
  note?: string | null;
} = {}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const inputId = tag
    ? `newsletter-email-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`
    : "newsletter-email";
  const light = variant === "light";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.includes("@")) {
      setError("Enter a valid email address.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setError(null);
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tag ? { email, tag } : { email }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("ok");
      pushEvent({
        event: "newsletter_signup",
        form_name: "newsletter",
        form_location: window.location.pathname,
      });
      setEmail("");
    } catch {
      setStatus("error");
      setError("Could not subscribe. Try again shortly.");
    }
  }

  return (
    <form onSubmit={onSubmit} className="w-full max-w-md" noValidate>
      <label htmlFor={inputId} className="sr-only">
        Email address
      </label>
      {/* Ivory pill input plus pill button (brief 5.7) */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          id={inputId}
          type="email"
          autoComplete="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "submitting" || status === "ok"}
          className={`flex-1 min-w-0 rounded-full text-ink placeholder:text-muted text-[14px] px-5 py-3 focus:outline-none focus-visible:outline-2 focus-visible:outline-gold disabled:opacity-60 ${
            light ? "bg-paper border border-ink/20" : "bg-ivory"
          }`}
        />
        <button
          type="submit"
          disabled={status === "submitting" || status === "ok"}
          // Non-booking pills are white and hover gold (sitewide hover
          // rule); the ink/20 border keeps the pill legible on light panels.
          className="silk-pill silk-pill--paper border-ink/20 shrink-0 disabled:opacity-60"
        >
          {status === "submitting"
            ? "Sending"
            : status === "ok"
              ? "Subscribed"
              : buttonLabel}
        </button>
      </div>
      {note ? (
        <p
          className={`mt-3 text-[11px] leading-[1.5] ${
            light ? "text-muted" : "text-taupe/70"
          }`}
        >
          {note}
        </p>
      ) : null}
      {status === "error" && error ? (
        <p className={`mt-2 text-[11px] ${light ? "text-ink/70" : "text-taupe"}`}>
          {error}
        </p>
      ) : null}
      {status === "ok" ? (
        <p className={`mt-2 text-[11px] ${light ? "text-ink/70" : "text-taupe"}`}>
          Thank you. You will hear from us shortly.
        </p>
      ) : null}
    </form>
  );
}
